import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  clearStoredAuthError,
  parseAuthError,
  readStoredAuthError,
  type CapturedAuthError,
  type StoredAuthError,
} from "@/utils/samlDiagnostic";

const CALLBACK_STALE_MS = 10 * 60 * 1000;

function isFreshCapture(st: StoredAuthError | null): boolean {
  if (!st?.capturedAt) return false;
  const t = Date.parse(st.capturedAt);
  if (Number.isNaN(t)) return true;
  return Date.now() - t < CALLBACK_STALE_MS;
}

function decodeOAuthParam(raw: string | null): string | null {
  if (raw == null) return null;
  try {
    return decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    return raw;
  }
}

type CallbackView =
  | { kind: "loading" }
  | { kind: "timeout" }
  | { kind: "error"; data: CallbackErrorData };

type CallbackErrorData = {
  source: "hash" | "stored";
  /** Every key from the URL hash (OAuth), if any. */
  hashEntries: [string, string][];
  /** Fields extracted from the message (AADSTS, trace, correlation, …). */
  parsed: CapturedAuthError;
  /** ISO time when the error was written to sessionStorage, if applicable. */
  storedCapturedAt?: string;
  /** stack from sessionStorage, when available */
  stack?: string | null;
};

/**
 * Entra redirects back here after an interactive sign-in. MSAL processes
 * the redirect in AuthProvider (handleRedirectPromise). SAML / AADSTS errors
 * often reach us only as a rejected promise + persisted object — the URL
 * hash may already be cleared — so we also read `readStoredAuthError()`.
 */
export function AuthCallbackPage() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<CallbackView>({ kind: "loading" });

  const tryFinishSuccess = useCallback((): boolean => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return false;

    let returnTo = "/myapps";
    try {
      const stored = sessionStorage.getItem("portal:returnTo");
      if (stored && stored.startsWith("/") && !stored.startsWith("//")) {
        returnTo = stored;
      }
      sessionStorage.removeItem("portal:returnTo");
    } catch {
      /* noop */
    }
    navigate(returnTo, { replace: true });
    return true;
  }, [instance, accounts, navigate]);

  useEffect(() => {
    if (tryFinishSuccess()) {
      return;
    }

    const hash = location.hash.replace(/^#/, "");
    const hashParams = new URLSearchParams(hash);
    const hasOAuthError = !!(
      hashParams.get("error") || hashParams.get("error_description")
    );

    const hashEntries: [string, string][] = [];
    hashParams.forEach((v, k) => {
      hashEntries.push([k, v]);
    });

    if (hasOAuthError) {
      const errParam = hashParams.get("error");
      const desc = decodeOAuthParam(hashParams.get("error_description"));
      const parsed = parseAuthError({
        errorCode: errParam,
        errorMessage: desc || errParam,
      });
      setView({
        kind: "error",
        data: {
          source: "hash",
          hashEntries,
          parsed,
        },
      });
      return;
    }

    const stored = readStoredAuthError();
    if (stored && isFreshCapture(stored) && (stored.errorMessage || stored.errorCode)) {
      const parsed = parseAuthError(stored);
      if (parsed.errorMessage || parsed.errorCode) {
        setView({
          kind: "error",
          data: {
            source: "stored",
            hashEntries,
            parsed,
            storedCapturedAt: stored.capturedAt,
            stack: stored.stack,
          },
        });
        return;
      }
    }

    setView({ kind: "loading" });

    const timeout = window.setTimeout(() => {
      setView((v) => (v.kind === "loading" ? { kind: "timeout" } : v));
    }, 10_000);

    const interval = window.setInterval(() => {
      if (tryFinishSuccess()) {
        window.clearTimeout(timeout);
        window.clearInterval(interval);
      }
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [instance, accounts, location.hash, location.search, navigate, tryFinishSuccess]);

  if (view.kind === "error") {
    return (
      <CallbackErrorView
        data={view.data}
        onBackToSignIn={() => {
          clearStoredAuthError();
          navigate("/signin", { replace: true });
        }}
        onClearAndRetry={() => {
          clearStoredAuthError();
          window.location.replace("/auth/callback" + (location.search || ""));
        }}
      />
    );
  }

  if (view.kind === "timeout") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="card max-w-md p-8 text-center">
          <h1 className="text-base font-semibold text-red-700">Sign-in error</h1>
          <p className="mt-2 text-sm text-slate-600">
            Sign-in is taking longer than expected. Please refresh the page or
            try again. If a SAML/Entra error just occurred, open this page with
            the same URL as the failed redirect; details appear when
            <code className="mx-0.5 kbd">handleRedirectPromise</code> can persist
            the error.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-ghost"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate("/signin", { replace: true })}
              className="btn-primary"
            >
              Back to sign-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="card max-w-md p-8 text-center">
        <div className="mx-auto inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <h1 className="mt-3 text-base font-semibold text-slate-900">
          Finishing sign-in…
        </h1>
        <p className="mt-1 text-sm text-slate-500">Redirecting you to your apps.</p>
      </div>
    </div>
  );
}

function CallbackErrorView({
  data,
  onBackToSignIn,
  onClearAndRetry,
}: {
  data: CallbackErrorData;
  onBackToSignIn: () => void;
  onClearAndRetry: () => void;
}) {
  const p = data.parsed;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="card border-red-200 p-5 text-left">
          <h1 className="text-lg font-semibold text-red-800">Sign-in error</h1>
          <p className="mt-1 text-sm text-slate-600">
            Source:{" "}
            <code className="kbd">
              {data.source === "hash" ? "OAuth URL hash" : "MSAL redirect (sessionStorage)"}
            </code>
            {data.storedCapturedAt ? (
              <span className="ml-2 text-xs text-slate-500">
                stored at {new Date(data.storedCapturedAt).toLocaleString()}
              </span>
            ) : null}
          </p>

          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[140px_1fr]">
            <dt className="font-medium text-slate-500">Error / AADSTS</dt>
            <dd className="break-all font-mono text-slate-900">
              {p.errorCode || "—"}
            </dd>
            {p.traceId ? (
              <>
                <dt className="font-medium text-slate-500">Trace ID</dt>
                <dd className="break-all font-mono text-slate-900">{p.traceId}</dd>
              </>
            ) : null}
            {p.correlationId ? (
              <>
                <dt className="font-medium text-slate-500">Correlation ID</dt>
                <dd className="break-all font-mono text-slate-900">
                  {p.correlationId}
                </dd>
              </>
            ) : null}
            {p.timestamp ? (
              <>
                <dt className="font-medium text-slate-500">Timestamp</dt>
                <dd className="font-mono text-slate-900">{p.timestamp}</dd>
              </>
            ) : null}
            {p.subError ? (
              <>
                <dt className="font-medium text-slate-500">subError</dt>
                <dd className="break-all font-mono text-slate-900">{p.subError}</dd>
              </>
            ) : null}
          </dl>

          {p.errorMessage ? (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Full message
              </div>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
                {p.errorMessage}
              </pre>
            </div>
          ) : null}

          {data.hashEntries.length > 0 ? (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase text-slate-500">
                OAuth response (URL hash) — all parameters
              </div>
              <div className="mt-1 overflow-x-auto rounded border border-slate-200 bg-white">
                <table className="w-full min-w-0 text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-slate-600">
                      <th className="p-2 font-semibold">Name</th>
                      <th className="p-2 font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.hashEntries.map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-50">
                        <td className="p-2 align-top font-mono text-slate-700">
                          {k}
                        </td>
                        <td className="max-w-0 p-2 break-all font-mono text-slate-800">
                          {k === "error_description" || k === "error_uri"
                            ? decodeOAuthParam(v) ?? v
                            : v}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded border border-amber-200 bg-amber-50/80 p-3 text-xs text-amber-950">
              <strong>OAuth hash</strong> is empty in the address bar (common after
              MSAL processes the redirect). The error text above was recovered from
              the MSAL <code className="kbd">handleRedirectPromise</code> failure
              stored for diagnostics.
            </div>
          )}

          {data.stack ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-slate-500">
                Client stack (debug)
              </summary>
              <pre className="mt-1 max-h-32 overflow-auto rounded bg-slate-100 p-2 text-[10px] text-slate-700">
                {data.stack}
              </pre>
            </details>
          ) : null}

          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <p className="font-semibold text-slate-800">SAML / assertion note</p>
            <p className="mt-1">
              The browser does <strong>not</strong> receive the raw SAML
              &lt;Assertion&gt; from Microsoft Entra — only OAuth / OIDC-style
              errors (e.g. <code className="kbd">invalid_request</code> with
              AADSTS in the description) after the realm exchange. Use the{" "}
              <strong>Trace ID</strong> and <strong>Correlation ID</strong> in
              Microsoft Entra sign-in logs, or a SAML trace at your identity
              provider, to inspect the assertion and claim mapping.
            </p>
            <p className="mt-2">
              For AADSTS5000819 (email / external realm), compare{" "}
              <code className="kbd">VITE_EXPECTED_EMAIL_DOMAIN</code> and the
              Tokens page SAML email diagnostic after a successful (non-federated)
              test sign-in, or fix NameID / emailaddress claims at the IdP per
              Entra External ID requirements.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={onBackToSignIn} className="btn-primary">
              Back to sign-in
            </button>
            {data.source === "stored" ? (
              <button
                type="button"
                onClick={onClearAndRetry}
                className="btn-ghost text-sm"
                title="Clear sessionStorage error and reload this page"
              >
                Clear stored error
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
